import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import { WRAPPER_TO_PROVIDER_EVENT_TYPES } from '@/features/event-handlers/wrapper-event-handlers/provider-events/types';
import { gameContextSelector } from '@/features/game-context/store/game-context.ts';
import { GenericProvider } from '@/features/game-context/types';
import { handleUnknownEvent } from '@/features/game-window-integration/helpers/generic-helpers';
import { PROVIDER_ADAPTORS } from '@/features/game-window-integration/providers/generic-provider/payload-adaptors';
import {
  GenericEvents,
  GenericEventsData,
} from '@/features/game-window-integration/providers/generic-provider/types.ts';
import { useEventHandlersAdaptor } from '@/features/game-window-integration/providers/generic-provider/use-event-handler-adaptor.ts';
import {
  payloadEventDataAdaptor,
  providerEventDataAdaptor,
  remapEvent,
} from '@/features/game-window-integration/providers/generic-provider/utils.ts';
import { ProviderLoggerMessages } from '@/features/game-window-integration/types';
import { DdEventType, Logger } from '@/features/logging/logger';
import * as LoggerModule from '@/features/logging/logger.ts';
import { WrapperProxy } from '@/features/wrapper-proxy/wrapper-proxy.ts';

export const useGenericProviderEventHandlers = (provider: GenericProvider) => {
  const { gameWindowUIEnabled } = useSelector(gameContextSelector);
  const { providerEventHandlerMapper, getGameProviderFrame } =
    useEventHandlersAdaptor(provider);

  const providerEventHandler = useCallback(
    ({ data }: { data: GenericEventsData }) => {
      // Only forward provider events once the launcher has signalled that the
      // game window UI is enabled (GL/GAME_WINDOW_UI_ENABLED). Remove this gate when iOS wrapper implement
      // window.sendGameWindowEnabled handshake
      if (
        !gameWindowUIEnabled &&
        provider === GenericProvider.FLUTTERSTUDIOS &&
        !WrapperProxy.isIosWrapper()
      )
        return;

      const parsedData =
        typeof data === 'string'
          ? (JSON.parse(data) as GenericEventsData)
          : data;

      const adaptedData = providerEventDataAdaptor(provider, parsedData);
      if (adaptedData?.type) {
        if (providerEventHandlerMapper[adaptedData.type]) {
          providerEventHandlerMapper[adaptedData.type]?.(adaptedData);
        } else {
          handleUnknownEvent(adaptedData);
        }
      }

      const reactiveMessages =
        PROVIDER_ADAPTORS[provider]?.getReactiveOutbound?.(
          parsedData,
          adaptedData,
        ) ?? [];

      if (reactiveMessages.length > 0) {
        const gameProviderFrame = getGameProviderFrame();
        if (gameProviderFrame) {
          reactiveMessages.forEach(({ event, notify, payload }) => {
            const envelope = payloadEventDataAdaptor(
              provider,
              event,
              notify,
              payload,
            );
            if (envelope) {
              gameProviderFrame.postMessage(envelope, { targetOrigin: '*' });
            }
          });
        }
      }
    },
    [
      provider,
      providerEventHandlerMapper,
      getGameProviderFrame,
      gameWindowUIEnabled,
    ],
  );

  const wrapperEventHandler = useCallback(
    (e: MessageEvent) => {
      const executeEvent = (event: GenericEventsData | undefined) => {
        if (!event) return;
        const gameProviderFrame = getGameProviderFrame();
        const options = { targetOrigin: '*' };

        if (gameProviderFrame) {
          gameProviderFrame.postMessage(event, options);
        } else {
          Logger.rumError({
            name: ProviderLoggerMessages.FAILED_HANDLER_FETCH.replace(
              '%{provider}',
              provider,
            ),
            useCase: LoggerModule.DdEventCategory.ProviderIntegration,
            eventType: DdEventType.Error,
            context: {
              provider,
            },
          });
        }
      };

      if (e.data.type === WRAPPER_TO_PROVIDER_EVENT_TYPES.RELOAD_BALANCE) {
        executeEvent(
          payloadEventDataAdaptor(
            provider,
            remapEvent(provider, GenericEvents.RELOAD_BALANCE),
            undefined,
            e.data?.payload,
          ),
        );
      }

      if (e.data.type === WRAPPER_TO_PROVIDER_EVENT_TYPES.AUTO_SPIN_STOP) {
        executeEvent(
          payloadEventDataAdaptor(
            provider,
            remapEvent(provider, GenericEvents.AUTOPLAY_STOP),
          ),
        );
      }

      if (e.data.type === WRAPPER_TO_PROVIDER_EVENT_TYPES.AUTO_SPIN_RESUME) {
        executeEvent(
          payloadEventDataAdaptor(
            provider,
            remapEvent(provider, GenericEvents.AUTOPLAY_RESUME),
          ),
        );
      }

      if (e.data.type === WRAPPER_TO_PROVIDER_EVENT_TYPES.TOGGLE_AUDIO) {
        const audioPayload = e.data.payload
          ? { mute: false, volume: 100 }
          : { mute: true, volume: null };
        executeEvent(
          payloadEventDataAdaptor(
            provider,
            remapEvent(provider, GenericEvents.TOGGLE_SOUND),
            true,
            audioPayload,
          ),
        );
      }

      if (e.data.type === WRAPPER_TO_PROVIDER_EVENT_TYPES.TOGGLE_PAYTABLE) {
        executeEvent(
          payloadEventDataAdaptor(
            provider,
            remapEvent(provider, GenericEvents.TOGGLE_PAYTABLE),
          ),
        );
      }

      if (e.data.type === WRAPPER_TO_PROVIDER_EVENT_TYPES.TOGGLE_HELP) {
        executeEvent(
          payloadEventDataAdaptor(
            provider,
            remapEvent(provider, GenericEvents.TOGGLE_HELP),
          ),
        );
      }

      if (e.data.type === WRAPPER_TO_PROVIDER_EVENT_TYPES.DISMISS_ERROR) {
        executeEvent(
          payloadEventDataAdaptor(
            provider,
            remapEvent(provider, GenericEvents.DISMISS_ERROR),
          ),
        );
        if (provider === GenericProvider.FLUTTERSTUDIOS) {
          WrapperProxy.setSpinInProgress(false);
        }
      }
    },
    [provider, getGameProviderFrame],
  );

  return { wrapperEventHandler, providerEventHandler };
};
